/**
 * Карточка кластера услуг: показывает состав кластера и drop-zone для услуг.
 * Дата: 16.03.2026
 */
import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge } from '@/components/ui';
import {
  ServiceCluster,
  ServiceClusterMember,
} from '@/api/service-clusters';
import { MedicalService } from '@/api/medicalServices';
import { Check, ChevronDown, ChevronRight, Power, Trash2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { getClusterColor, getClusterIconGlyph } from './clusterVisuals';
import { ClusterTriggerBindingsCard } from './ClusterTriggerBindingsCard';

interface ClusterCardProps {
  cluster: ServiceCluster;
  services: ServiceClusterMember[];
  draggedService: MedicalService | null;
  onDrop: (clusterId: string, serviceId: string) => void;
  onEdit?: (cluster: ServiceCluster) => void;
  onDelete?: (clusterId: string) => void;
  onToggleActive?: (cluster: ServiceCluster) => void;
  onOpenServices?: (cluster: ServiceCluster) => void;
  isExpanded?: boolean;
  isSelected?: boolean;
  onToggleExpand?: (clusterId: string) => void;
  onSelect?: (clusterId: string) => void;
  originState?: 'canonical' | 'custom' | 'overridden';
  className?: string;
}

function getSyncOriginBadge(syncOrigin?: string) {
  switch (syncOrigin) {
    case 'local_manual':
      return { label: 'Локальный override', className: 'bg-amber-50 text-amber-700' };
    case 'datalayer_manual':
      return { label: 'Data Layer manual', className: 'bg-sky-50 text-sky-700' };
    case 'datalayer_auto':
      return { label: 'Data Layer auto', className: 'bg-indigo-50 text-indigo-700' };
    default:
      return null;
  }
}

function formatSyncDate(value?: string) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString('ru-RU');
}

export function ClusterCard({
  cluster,
  services,
  draggedService,
  onDrop,
  onEdit,
  onDelete,
  onToggleActive,
  onOpenServices,
  isExpanded = false,
  isSelected = false,
  onToggleExpand,
  onSelect,
  originState = 'custom',
  className,
}: ClusterCardProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const clusterColor = getClusterColor(cluster.color);
  const clusterIcon = getClusterIconGlyph(cluster.icon);
  const syncOriginBadge = getSyncOriginBadge(cluster.sync_origin);
  const lastSyncedAt = formatSyncDate(cluster.last_synced_at);
  const dataLayerUpdatedAt = formatSyncDate(cluster.datalayer_updated_at);
  const localOverrideAt = formatSyncDate(cluster.local_manual_override_at);
  const originBadge = originState === 'canonical'
    ? { label: 'Канонический', className: 'bg-emerald-50 text-emerald-700' }
    : originState === 'overridden'
      ? { label: 'Переопределён', className: 'bg-amber-50 text-amber-700' }
      : { label: 'Кастомный', className: 'bg-slate-100 text-slate-700' };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedService) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (draggedService) {
      // Проверяем, не находится ли услуга уже в кластере
      const isAlreadyInCluster = services.some(m => m.service_id === draggedService.id);
      if (!isAlreadyInCluster) {
        onDrop(cluster.id, draggedService.id);
      }
    }
  };

  return (
    <Card
      id={`cluster-${cluster.id}`}
      className={cn(
        'transition-all border-l-4',
        isDragOver && 'border-2 border-primary-500 bg-primary-50',
        isSelected && 'ring-2 ring-primary-200 shadow-sm',
        className
      )}
      style={{ borderLeftColor: clusterColor }}
    >
      <CardHeader className="space-y-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => onToggleExpand?.(cluster.id)}
            className="mt-0.5 rounded-md p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
            aria-label={isExpanded ? 'Свернуть кластер' : 'Раскрыть кластер'}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              onSelect?.(cluster.id);
            }}
            className="flex flex-1 items-start gap-3 text-left"
          >
            <span
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-xl"
              style={{ backgroundColor: `${clusterColor}18` }}
            >
              {clusterIcon}
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-lg">
                  <span style={{ color: clusterColor }}>{cluster.name}</span>
                </CardTitle>
                {!cluster.is_active && (
                  <Badge variant="secondary" className="text-xs">
                    Неактивен
                  </Badge>
                )}
                <Badge className={`text-xs ${originBadge.className}`}>
                  {originBadge.label}
                </Badge>
                {syncOriginBadge && (
                  <Badge className={`text-xs ${syncOriginBadge.className}`}>
                    {syncOriginBadge.label}
                  </Badge>
                )}
                {isSelected && (
                  <Badge variant="success" className="text-xs">
                    <Check className="mr-1 h-3 w-3" />
                    Фильтр активен
                  </Badge>
                )}
              </div>

              {cluster.description && (
                <p className="mt-1 text-sm text-neutral-600">{cluster.description}</p>
              )}

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {cluster.service_count ?? services.length} услуг
                </Badge>
                {cluster.registry_key && (
                  <Badge variant="secondary" className="text-xs">
                    preset: {cluster.registry_key}
                  </Badge>
                )}
                {cluster.display_order > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    Порядок {cluster.display_order}
                  </Badge>
                )}
                {cluster.datalayer_cluster_external_id && (
                  <Badge variant="secondary" className="text-xs">
                    DL: {cluster.datalayer_cluster_external_id}
                  </Badge>
                )}
              </div>
              {(lastSyncedAt || dataLayerUpdatedAt || localOverrideAt) && (
                <div className="mt-2 space-y-1 text-xs text-neutral-500">
                  {lastSyncedAt && <div>Последний sync: {lastSyncedAt}</div>}
                  {dataLayerUpdatedAt && <div>Обновлено в Data Layer: {dataLayerUpdatedAt}</div>}
                  {localOverrideAt && <div>Локальный override: {localOverrideAt}</div>}
                </div>
              )}
            </div>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {onSelect && (
            <Button
              variant={isSelected ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => onSelect(cluster.id)}
              disabled={!cluster.is_active}
            >
              {isSelected ? 'Показаны услуги кластера' : 'Показать услуги кластера'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onToggleExpand?.(cluster.id)}
          >
            {isExpanded ? 'Свернуть детали' : 'Раскрыть детали'}
          </Button>
          {onOpenServices && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onOpenServices(cluster)}
            >
              Открыть состав
            </Button>
          )}
          {onToggleActive && (
            <Button
              variant={cluster.is_active ? 'secondary' : 'primary'}
              size="sm"
              onClick={() => onToggleActive(cluster)}
              className={cluster.is_active ? 'text-amber-700 hover:text-amber-800' : undefined}
            >
              <Power className="mr-1 h-4 w-4" />
              {cluster.is_active ? 'Деактивировать' : 'Активировать'}
            </Button>
          )}
        </div>

        {!isExpanded && (
          <div className="space-y-3 rounded-xl border border-neutral-200 bg-neutral-50/80 p-3">
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Услуги
              </p>
              {services.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {services.slice(0, 3).map((member) => (
                    <Badge key={member.id} variant="secondary" className="text-xs">
                      {member.service?.name || 'Без названия'}
                    </Badge>
                  ))}
                  {services.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{services.length - 3} ещё
                    </Badge>
                  )}
                </div>
              ) : (
                <p className="text-sm text-neutral-500">Пока без услуг</p>
              )}
            </div>
          </div>
        )}
      </CardHeader>

      {isExpanded && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <CardContent className="space-y-4 pt-0">
            <div
              className={cn(
                'rounded-lg border-2 border-dashed p-4 text-center transition-colors',
                isDragOver
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-300 hover:border-neutral-400',
                draggedService && !services.some((m) => m.service_id === draggedService.id)
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed opacity-50'
              )}
            >
              {draggedService && !services.some((m) => m.service_id === draggedService.id) ? (
                <p className="text-sm text-neutral-600">
                  Перетащите услугу "{draggedService.name}" сюда
                </p>
              ) : (
                <p className="text-sm text-neutral-500">
                  Перетащите услугу сюда для добавления в кластер
                </p>
              )}
            </div>

            <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h4 className="text-sm font-medium text-neutral-700">Состав кластера</h4>
                  <p className="mt-1 text-sm text-neutral-500">
                    {services.length > 0
                      ? `${services.length} услуг. Полный CRUD состава открыт в отдельном окне.`
                      : 'Внутри кластера пока нет услуг.'}
                  </p>
                </div>
                {onOpenServices && (
                  <Button variant="primary" size="sm" onClick={() => onOpenServices(cluster)}>
                    Управлять услугами
                  </Button>
                )}
              </div>
            </div>

            <ClusterTriggerBindingsCard clusterId={cluster.id} />

            {(onEdit || onDelete) && (
              <div className="flex items-center gap-2 border-t border-neutral-200 pt-2">
                {onEdit && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onEdit(cluster)}
                    className="flex-1"
                  >
                    Редактировать
                  </Button>
                )}
                {onDelete && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      if (confirm(`Удалить кластер "${cluster.name}"?`)) {
                        onDelete(cluster.id);
                      }
                    }}
                    className="flex-1 text-error hover:text-red-700"
                  >
                    <Trash2 className="mr-1 h-4 w-4" />
                    Удалить
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </div>
      )}
    </Card>
  );
}
